import { Injectable } from '@angular/core';
import { Observable, Subject, map, tap } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { LoggerService } from '../../services/logger.service';
import { ApiResponse } from '../../shared/types';
import {
  CustomDiscountCustomerOption,
  CustomDiscountListParams,
  CustomDiscountRow,
  Discount,
  DiscountAvailability,
  DiscountListParams,
  DiscountListResult,
  DiscountPayload,
} from '../models/discount.models';
import { DEFAULT_DISCOUNT_PAGE_SIZE } from '../constants/discount.constants';

type QueryScalar = string | number | boolean;
type QueryValue = QueryScalar | QueryScalar[] | null | undefined;
type BuiltQueryValue = QueryScalar | readonly QueryScalar[];

/**
 * Discount module API client (CWS-1176).
 *
 * Discount definitions live in svc-package (`package/discounts`); the Custom Discount
 * tab reads order-level rows from svc-order (`order/custom-discounts`), which owns the
 * orders/customers joins.
 *
 * `PackageService.getDiscounts()` stays as-is for the HTTP fetch used by Order Request
 * tiles. After create/update/delete, `clearDiscountsCache()` notifies listeners (e.g.
 * Order Request catalog) so they drop stale discount lists — same pattern as packages.
 */
@Injectable({ providedIn: 'root' })
export class DiscountService {
  private readonly discountsEndpoint = 'package/discounts';
  private readonly discountsCsvEndpoint = 'package/discounts/download/csv';
  private readonly customDiscountsEndpoint = 'order/custom-discounts';
  private readonly customDiscountsCsvEndpoint = 'order/download/custom-discounts-csv';

  /** Notifies listeners (e.g. Order Request catalog) when discount data changes. */
  private readonly discountsCacheClearedSubject = new Subject<void>();
  readonly discountsCacheCleared$ = this.discountsCacheClearedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private logger: LoggerService
  ) {}

  /** Drops empty values so the API never receives `search=` or `page=undefined`. */
  private buildParams(params: Record<string, QueryValue>): Record<string, BuiltQueryValue> {
    return Object.entries(params).reduce<Record<string, BuiltQueryValue>>((acc, [key, value]) => {
      const serialized = this.serializeQueryValue(value);
      if (serialized === undefined) {
        return acc;
      }
      acc[key] = serialized;
      return acc;
    }, {});
  }

  /**
   * Scalars pass through; multi-select arrays are kept as arrays so Angular emits
   * repeated query keys (e.g. `discount_type=flat&discount_type=percentage`).
   */
  private serializeQueryValue(value: QueryValue): BuiltQueryValue | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    if (Array.isArray(value)) {
      const items = value.filter(
        (item): item is QueryScalar =>
          item !== null && item !== undefined && !(typeof item === 'string' && item.trim() === '')
      );
      if (items.length === 0) {
        return undefined;
      }
      return items;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return String(value);
    }

    const asString = String(value).trim();
    return asString === '' ? undefined : asString;
  }

  private readPagination(
    response: ApiResponse<unknown> & { pagination?: Record<string, unknown> },
    rowCount: number,
    requestedPage: number,
    requestedLimit: number
  ): DiscountListResult<never>['pagination'] {
    const pagination = response?.pagination || {};
    const total = Number(pagination['total'] ?? rowCount) || 0;
    const limit = Number(pagination['limit'] ?? requestedLimit) || requestedLimit;
    const page = Number(pagination['page'] ?? requestedPage) || requestedPage;
    const totalPages =
      Number(pagination['total_pages'] ?? 0) || (limit > 0 ? Math.ceil(total / limit) : 0);
    return { total, page, limit, total_pages: totalPages };
  }

  /** Discounts Summary tab. */
  getDiscounts(params: DiscountListParams = {}): Observable<DiscountListResult<Discount>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? DEFAULT_DISCOUNT_PAGE_SIZE;
    const query = this.buildParams({ ...params, page, limit });

    return this.apiService.get<Discount[]>(this.discountsEndpoint, query).pipe(
      map(response => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        return {
          rows,
          pagination: this.readPagination(response, rows.length, page, limit),
        } as DiscountListResult<Discount>;
      })
    );
  }

  /**
   * Discounts Summary CSV. Honours the same filters as the list endpoint and
   * returns the full filtered set (not the visible page).
   */
  downloadDiscountsCsv(params: Omit<DiscountListParams, 'page' | 'limit'>): Observable<Blob> {
    this.logger.debug('Downloading discounts summary CSV', params);
    return this.apiService.getBlob(this.discountsCsvEndpoint, this.buildParams({ ...params }));
  }

  getDiscountById(id: string): Observable<ApiResponse<Discount>> {
    return this.apiService.get<Discount>(`${this.discountsEndpoint}/${id}`);
  }

  createDiscount(payload: DiscountPayload): Observable<ApiResponse<Discount>> {
    return this.apiService.post<Discount>(this.discountsEndpoint, payload).pipe(
      tap(() => {
        // Custom (per-order) discounts must not invalidate the Order Request shop-discount tiles.
        if (payload.type !== 'custom') {
          this.clearDiscountsCache();
        }
      })
    );
  }

  /**
   * Edit a discount. The API soft-deletes the current row and returns a NEW version
   * with a new id, so callers must refresh rather than patch their cached row.
   */
  updateDiscount(id: string, payload: DiscountPayload): Observable<ApiResponse<Discount>> {
    return this.apiService.patch<Discount>(`${this.discountsEndpoint}/${id}`, payload).pipe(
      tap(() => {
        this.clearDiscountsCache();
      })
    );
  }

  deleteDiscount(id: string): Observable<ApiResponse<unknown>> {
    return this.apiService.delete(`${this.discountsEndpoint}/${id}`).pipe(
      tap(() => {
        this.clearDiscountsCache();
      })
    );
  }

  /**
   * Invalidate Order Request / other discount catalogs after create, update, or delete.
   * DiscountService itself does not cache list rows; this only notifies consumers.
   */
  clearDiscountsCache(): void {
    this.discountsCacheClearedSubject.next();
  }

  /** Name/code uniqueness check for the Create form. `excludeId` skips the row being edited. */
  checkAvailability(options: {
    name?: string;
    code?: string;
    excludeId?: string;
  }): Observable<ApiResponse<DiscountAvailability>> {
    return this.apiService.get<DiscountAvailability>(
      `${this.discountsEndpoint}/availability`,
      this.buildParams({ ...options })
    );
  }

  /** Custom Discount tab. */
  getCustomDiscounts(
    params: CustomDiscountListParams = {}
  ): Observable<DiscountListResult<CustomDiscountRow>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? DEFAULT_DISCOUNT_PAGE_SIZE;
    const query = this.buildParams({ ...params, page, limit });

    return this.apiService.get<CustomDiscountRow[]>(this.customDiscountsEndpoint, query).pipe(
      map(response => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const customers = this.readCustomDiscountCustomers(response);
        return {
          rows,
          pagination: this.readPagination(response, rows.length, page, limit),
          customers,
        } as DiscountListResult<CustomDiscountRow>;
      })
    );
  }

  /**
   * Distinct customers for the Customer filter — same join/name source as the
   * Customer Name column on `GET order/custom-discounts`.
   */
  private readCustomDiscountCustomers(
    response: ApiResponse<unknown> & { customers?: unknown }
  ): CustomDiscountCustomerOption[] {
    const raw = Array.isArray(response?.customers) ? response.customers : [];
    const byId = new Map<string, CustomDiscountCustomerOption>();
    for (const item of raw) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const row = item as { id?: unknown; label?: unknown };
      const id = String(row.id ?? '').trim();
      if (!id) {
        continue;
      }
      const label = String(row.label ?? '').trim() || id;
      byId.set(id, { id, label });
    }
    return [...byId.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }

  /**
   * Custom Discount CSV. Honours the active filters and returns the full filtered
   * set (not the visible page). Pass one location_id or every location for
   * "All Locations" (API requires at least one).
   */
  downloadCustomDiscountsCsv(
    params: Omit<CustomDiscountListParams, 'page' | 'limit' | 'sort_by' | 'order_by'> & {
      location_id: string | string[];
    }
  ): Observable<Blob> {
    this.logger.debug('Downloading custom discounts CSV', params);
    return this.apiService.getBlob(
      this.customDiscountsCsvEndpoint,
      this.buildParams({ ...params })
    );
  }
}
